import React, { useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Calendar, Clock, ExternalLink } from 'lucide-react';

/**
 * Calendly Integration Component
 * 
 * Usage:
 * <CalendlyWidget 
 *   calendlyUrl="https://calendly.com/your-username/30min"
 *   type="inline" // or "popup" or "badge"
 * />
 */

const CalendlyWidget = ({ 
  calendlyUrl, 
  type = 'inline',
  text = 'Schedule a Meeting',
  prefill = {},
  pageSettings = {}
}) => {
  
  useEffect(() => {
    // Load Calendly script
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);

    // Load Calendly CSS
    const link = document.createElement('link');
    link.href = 'https://assets.calendly.com/assets/external/widget.css';
    link.rel = 'stylesheet';
    document.head.appendChild(link);

    return () => {
      // Cleanup
      document.body.removeChild(script);
      document.head.removeChild(link);
    };
  }, []);

  useEffect(() => {
    // Initialize Calendly widget after script loads
    if (window.Calendly && type === 'badge') {
      window.Calendly.initBadgeWidget({
        url: calendlyUrl,
        text: text,
        color: '#0069ff',
        textColor: '#ffffff',
        branding: false
      });
    }
  }, [calendlyUrl, type, text]);

  const handlePopupClick = (e) => {
    e.preventDefault();
    if (window.Calendly) {
      window.Calendly.initPopupWidget({
        url: calendlyUrl,
        prefill: prefill,
        pageSettings: pageSettings
      });
    }
  };

  if (type === 'inline') {
    return (
      <Card className="overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Schedule a Meeting
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div 
            className="calendly-inline-widget" 
            data-url={calendlyUrl}
            style={{ minWidth: '320px', height: '700px' }}
          />
        </CardContent>
      </Card>
    );
  }

  if (type === 'popup') {
    return (
      <button
        onClick={handlePopupClick}
        className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl"
      >
        <Calendar className="w-5 h-5" />
        {text}
      </button>
    );
  }

  if (type === 'badge') {
    // Badge appears as floating button on bottom right
    return null; // Calendly badge is injected automatically
  }

  return null;
};

/**
 * Calendly Link Component - Simple link to Calendly page
 */
export const CalendlyLink = ({ calendlyUrl, text = 'Schedule with Calendly', className = '' }) => {
  return (
    <a
      href={calendlyUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium ${className}`}
    >
      <ExternalLink className="w-4 h-4" />
      {text}
    </a>
  );
};

/**
 * Calendly Schedule Button - Opens Calendly in new tab
 */
export const CalendlyScheduleButton = ({ 
  calendlyUrl, 
  text = 'Schedule a Meeting',
  variant = 'primary'
}) => {
  const baseClasses = "inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all shadow-md hover:shadow-lg";
  
  const variants = {
    primary: "bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700",
    secondary: "bg-white text-blue-600 border-2 border-blue-600 hover:bg-blue-50",
    outline: "bg-transparent text-blue-600 border border-blue-600 hover:bg-blue-50"
  };

  return (
    <a
      href={calendlyUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={`${baseClasses} ${variants[variant]}`}
    >
      <Calendar className="w-5 h-5" />
      {text}
    </a>
  );
};

/**
 * Calendly Meeting Info Card
 */
export const CalendlyMeetingCard = ({ 
  calendlyUrl,
  title = "Book a Meeting",
  description = "Schedule a time that works best for you",
  duration = "30 min",
  features = []
}) => {
  return (
    <Card className="hover:shadow-lg transition-shadow">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="w-6 h-6 text-blue-600" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-gray-600">{description}</p>
        
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Clock className="w-4 h-4" />
          <span>{duration}</span>
        </div>

        {features.length > 0 && (
          <ul className="space-y-2">
            {features.map((feature, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                <span className="text-green-600 mt-0.5">✓</span>
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        )}

        <CalendlyScheduleButton 
          calendlyUrl={calendlyUrl}
          text="Schedule Now"
          variant="primary"
        />
      </CardContent>
    </Card>
  );
};

export default CalendlyWidget;
